import { NgxPageScrollModule } from 'ngx-page-scroll';
import { MapUsecase } from 'src/app/dashboard/usecases/map.usecase';
import { environment } from 'src/environments/environment';
import { L } from 'stroly-js';

import { Location, NgStyle } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  input,
  viewChild,
} from '@angular/core';
import type { OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { AuthService } from 'src/app/dashboard/services/auth.service';
import type { AuthedUserProfile } from 'src/app/dashboard/services/authed-user-profile.service';
import { AuthedUserProfileService } from 'src/app/dashboard/services/authed-user-profile.service';
import { JwtAuthService } from 'src/app/dashboard/services/jwt-auth.service';
import { LoadingService } from 'src/app/shared/services/loading.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { ViewportService } from 'src/app/shared/services/viewport.service';

import { DateFilterComponent } from '../../pages/details/details-page/date-filter/date-filter.component';
import { DataTypeFilterComponent } from './data-type-filter/data-type-filter.component';
import { DownloadButtonComponent } from './download-button/download-button.component';

// import { DownloadButtonComponent } from './download-button/download-button.component';
@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  providers: [Location],
  imports: [
    RouterLink,
    DataTypeFilterComponent,
    DateFilterComponent,
    DownloadButtonComponent,
    NgStyle,
    NgxPageScrollModule,
  ],
})
export class ToolbarComponent implements OnInit {
  // Inject services
  private readonly authService = inject(AuthService);
  private readonly loadingService = inject(LoadingService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly mapUsecase = inject(MapUsecase);
  private readonly jwtAuth = inject(JwtAuthService);
  private readonly translate = inject(TranslateService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly authedUserProfileService = inject(AuthedUserProfileService);
  private readonly router = inject(Router);
  private readonly viewportService = inject(ViewportService);

  userMenuActive = false;
  enablePCMode = sessionStorage.getItem('enablePCMode');
  isMobile =
    L.Browser.mobile &&
    !this.enablePCMode &&
    window.parent.screen.width < 1024 &&
    !(
      window.parent.screen.height >= 1024 &&
      window.orientation.toString().indexOf('90') != -1
    );
  under1024px =
    L.Browser.mobile &&
    this.enablePCMode &&
    window.parent.screen.width < 1024 &&
    !(
      window.parent.screen.height >= 1024 &&
      window.orientation.toString().indexOf('90') != -1
    );

  toTopButtonVisible = false;
  hasAuthedUserProfile = false;
  windowHeight = window.innerHeight > 1000 ? 1000 : window.innerHeight;
  language =
    (window.navigator.languages && window.navigator.languages[0]) ||
    window.navigator.language;

  // 翻訳済みテキスト
  labels = {
    dataReport: '',
    logOut: '',
    myPage: '',
    editProfile: '',
    newMap: '',
    newBoard: '',
    home: '',
    search: '',
    helpGuide: '',
    terms: '',
    privacyPolicy: '',
    operatingCompany: '',
    changePCMode: '',
    changeSPMode: '',
    logoutErrorMessage: '',
    mapTitleHeader: '',
  };

  loading = true;
  isScrollRunning = false;

  isDetailPage = false;
  mapID: string | null = null;
  mapTitle = '';
  mapUrl = '';

  authApiHost = environment.authApiHost;
  strolyURL = environment.strolyURL;

  scrollDuration = 250; // クリック時のスクロール時間(msec)

  get active(): { [key: string]: string } {
    return {
      display: this.userMenuActive ? 'block' : 'none',
    };
  }

  get pcMode(): { [key: string]: string } {
    return {
      position: this.isMobile ? 'inherit' : 'relative',
    };
  }

  get toTopBtnPos(): { [key: string]: string } {
    let toTopBtnMargin: string;
    if (window.innerWidth > 1300) {
      toTopBtnMargin = location.pathname !== '/' ? '1240px' : '1210px';
    } else {
      toTopBtnMargin = (window.innerWidth - 130).toString() + 'px';
    }

    return {
      marginLeft: toTopBtnMargin,
    };
  }

  constructor() {
    if (
      this.enablePCMode ||
      (window.parent.screen.width >= 1024 && L.Browser.mobile) ||
      (L.Browser.mobile &&
        window.parent.screen.height >= 1024 &&
        window.orientation.toString().indexOf('90') != -1)
    ) {
      this.viewportService.setForPCMode();
    }

    this.jwtAuth.setHostAddress(environment.authApiHost);
  }

  private initLabels(): void {
    this.translate
      .get([
        'toolbar.DataReport',
        'toolbar.LogOut',
        'toolbar.MyPage',
        'toolbar.EditProfile',
        'toolbar.NewMap',
        'toolbar.NewBoard',
        'toolbar.Home',
        'toolbar.Search',
        'toolbar.HelpGuide',
        'toolbar.Terms',
        'toolbar.PrivacyPolicy',
        'toolbar.OperatingCompany',
        'toolbar.ChangePCMode',
        'toolbar.ChangeSPMode',
        'toolbar.mapTitleHeader',
        'error.logout',
      ])
      .subscribe((translations) => {
        this.labels.dataReport = environment.production
          ? translations['toolbar.DataReport']
          : translations['toolbar.DataReport'] + `(${environment.envName})`;
        this.labels.logOut = translations['toolbar.LogOut'];
        this.labels.myPage = translations['toolbar.MyPage'];
        this.labels.editProfile = translations['toolbar.EditProfile'];
        this.labels.newMap = translations['toolbar.NewMap'];
        this.labels.newBoard = translations['toolbar.NewBoard'];
        this.labels.home = translations['toolbar.Home'];
        this.labels.search = translations['toolbar.Search'];
        this.labels.helpGuide = translations['toolbar.HelpGuide'];
        this.labels.terms = translations['toolbar.Terms'];
        this.labels.privacyPolicy = translations['toolbar.PrivacyPolicy'];
        this.labels.operatingCompany = translations['toolbar.OperatingCompany'];
        this.labels.changeSPMode = translations['toolbar.ChangeSPMode'];
        this.labels.changePCMode = translations['toolbar.ChangePCMode'];
        this.labels.logoutErrorMessage = translations['error.logout'];
        this.labels.mapTitleHeader = translations['toolbar.mapTitleHeader'];

        this.cd.detectChanges(); // 404の時に表示が欠ける不具合の対策
      });
  }

  async ngOnInit(): Promise<void> {
    await this.authedUserProfileService.initAuthedUserProfile();
    if (this.authedUserProfile) {
      this.hasAuthedUserProfile = true;
      this.loading = false;
      this.cd.markForCheck();
    } else {
      this.loading = false;
      this.cd.markForCheck();
    }

    // 現在のパスをチェックしてツールバーの表示を制御
    this.router.events.subscribe(() => {
      const currentUrl = this.router.url;
      this.isDetailPage = currentUrl.includes('/details/');

      if (this.isDetailPage) {
        this.updateMapDetails();
      } else {
        this.resetMapDetails();
      }
    });

    // 現在のルートが詳細ページならば初期化時に情報を取得
    if (this.router.url.includes('/details/')) {
      this.isDetailPage = true;
      this.updateMapDetails();
    }

    this.initLabels();

    window.addEventListener('scroll', () => {
      if (!this.isScrollRunning) {
        this.isScrollRunning = true;

        // 描画する前のタイミングで呼び出してもらう
        window.requestAnimationFrame(() => {
          this.visibleToTopButton();
          this.cd.markForCheck();
          this.isScrollRunning = false;
        });
      }
    });
  }

  /**
   * 詳細ページの地図情報を取得
   */
  private async updateMapDetails(): Promise<void> {
    // URLからmapIDを抽出 (例: /details/123 → "123" または /details/123/gps → "123")
    const segments = this.router.url.split('/');
    const detailsIndex = segments.findIndex((segment) => segment === 'details');

    if (detailsIndex >= 0 && segments.length > detailsIndex + 1) {
      this.mapID = segments[detailsIndex + 1];
      const mapDetail = await this.mapUsecase.getMapDetailSummary(this.mapID);
      this.mapTitle = mapDetail.title;
      this.mapUrl = mapDetail.url;
    }
  }

  /**
   * マップ詳細情報をリセット
   */
  private resetMapDetails(): void {
    this.mapID = null;
    this.mapTitle = '';
    this.mapUrl = '';
  }

  get authedUserProfile(): AuthedUserProfile {
    return this.authedUserProfileService.getAuthedUserProfile();
  }

  get avatarUrl(): string {
    return this.authedUserProfileService.getAvatarUrl();
  }

  logout(): void {
    this.loadingService.start();

    try {
      this.authService.logout();
    } catch (error) {
      console.error(error);
      this.loadingService.end();
      this.snackbarService.openSnackbar(this.labels.logoutErrorMessage);
    }
  }

  toggleUserMenu(): void {
    this.userMenuActive = !this.userMenuActive;
    if (this.isMobile) {
      const container = document.getElementsByClassName('container')[0];
      if (this.userMenuActive) {
        container.setAttribute('style', 'display: none;');
      } else {
        container.removeAttribute('style');
      }
    }
  }

  changeToPCMode(): void {
    sessionStorage.setItem('enablePCMode', 'true');
    location.reload();
  }

  changeToSPMode(): void {
    sessionStorage.removeItem('enablePCMode');
    location.reload();
  }

  visibleToTopButton(): void {
    this.toTopButtonVisible = window.pageYOffset >= this.windowHeight;
  }
}
